// Browser Desk console: theme-aware colours and the small pieces every part of the page shares.
// Light keeps the original pastel fills; dark tints the paper with the accent and uses the
// accent's pale shade as text, so both clear 4.5:1.

import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { BAD, GREY, OK, WARN } from './deskModel';

// WARN as text or under white text is under 4.5:1; this darker amber clears it.
export const WARN_TEXT = '#b45309';
export const MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
export const TEST = '#7c3aed'; // test (scratch) runs: violet, distinct from every status colour

// One colour per prod desk, used on its card, its rows' dot and its Today lane.
const DESK_PALETTE = [
    { dot: '#5b9cf6', bg: '#e8f0fe', fg: '#1d4ed8', border: '#a9c6fa' },
    { dot: '#f0a35e', bg: '#fdf0e4', fg: '#9a4f0e', border: '#f3c597' },
    { dot: '#3fb5a3', bg: '#e3f6f3', fg: '#11695c', border: '#9edbd1' },
    { dot: '#d77fb4', bg: '#fbeaf4', fg: '#8e2f68', border: '#eeb5d6' },
    { dot: '#9ab84a', bg: '#f1f7e4', fg: '#4e6a12', border: '#cbe09a' },
];
export const DEV_COLOUR = { dot: '#9b8fd6', bg: '#efedf9', fg: '#4f4596', border: '#cbc5ec' };
export const deskColour = (n) => (n === null || n === undefined ? DEV_COLOUR : DESK_PALETTE[n % DESK_PALETTE.length]);

export const useTones = () => {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const muted = theme.palette.text.secondary;
    return {
        dark,
        muted,
        soft: dark ? theme.palette.text.secondary : '#555',
        strong: dark ? theme.palette.text.primary : '#333',
        ok: dark ? '#81c784' : OK,
        warn: dark ? '#ffb74d' : WARN_TEXT,
        bad: dark ? '#ef9a9a' : BAD,
        test: dark ? '#c4b5fd' : TEST,
        // Solid status chip fill under white text.
        fill: (colour) => (colour === WARN ? WARN_TEXT : colour),
        desk: (n) => {
            const c = deskColour(n);
            return dark ? { dot: c.dot, bg: alpha(c.dot, 0.16), fg: c.border, border: alpha(c.dot, 0.6) } : c;
        },
        chip: (colour, light) => (dark ? { bgcolor: alpha(colour, 0.22), color: { [BAD]: '#ef9a9a', [WARN]: '#ffb74d', [TEST]: '#c4b5fd' }[colour] || theme.palette.text.primary }
            : { bgcolor: light, color: colour === WARN ? WARN_TEXT : colour }),
        // Status text colours in their readable shade for this mode.
        text: (colour) => (dark ? { [OK]: '#81c784', [WARN]: '#ffb74d', [BAD]: '#ef9a9a', [GREY]: muted, [TEST]: '#c4b5fd' }[colour] || colour
            : { [WARN]: WARN_TEXT, [GREY]: muted }[colour] || colour),
    };
};

const LIGHT_FILL = { [OK]: '#e6f4ea', [WARN]: '#fff4e5', [BAD]: '#fdecea', [GREY]: '#f0f0f0', [TEST]: '#f1ebfe' };

export const Dot = ({ colour, size = 10 }) => (
    <Box component="span" sx={{ width: size, height: size, borderRadius: '50%', bgcolor: colour, display: 'inline-block', flex: 'none' }} />
);

// A small soft chip: Live / Test / Partial / override ...
export const Tag = ({ label, colour = GREY, title, outlined }) => {
    const t = useTones();
    const chip = (
        <Chip size="small" label={label} variant={outlined ? 'outlined' : 'filled'} className="d-font"
            sx={{ height: 20, fontSize: 11, fontWeight: 600, ...(outlined ? { color: t.text(colour), borderColor: t.text(colour) } : t.chip(colour, LIGHT_FILL[colour] || '#f0f0f0')) }} />
    );
    return title ? <Tooltip title={title}><span>{chip}</span></Tooltip> : chip;
};

// Segmented meter: `used` filled cells out of `of`, coloured ok or bad.
export const Meter = ({ used, of, colour = OK, label }) => {
    const t = useTones();
    const n = Math.max(0, Math.min(of || 0, 12));
    return (
        <Box role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={of} aria-valuenow={used}
            sx={{ display: 'flex', gap: '2px', mt: '3px' }}>
            {Array.from({ length: n }).map((_, i) => (
                <Box key={i} component="span" sx={{
                    width: 9, height: 6, borderRadius: '2px',
                    bgcolor: i < used ? t.text(colour) : (t.dark ? 'rgba(255,255,255,0.14)' : '#e3e5e8'),
                }} />
            ))}
        </Box>
    );
};
